import { Controller } from "@hotwired/stimulus";

// Handles form validation and conditional question visibility for intake forms
// eslint-disable-next-line import/no-default-export
export default class extends Controller {
  static targets = ["submitButton", "question", "moreInfo", "disclaimer"];

  connect() {
    this.validateForm();
    this.updateConditionalQuestions();
    this.updateMoreInfoContainers();
    this.updateDisclaimerContainers();
    this.initBmiCalculator();
    this.initExclusiveAnswers();
    this.initFileUploadHandlers();
  }

  // Called on any form field change
  fieldChanged() {
    this.validateForm();
    this.updateConditionalQuestions();
    this.updateMoreInfoContainers();
    this.updateDisclaimerContainers();
    this.updateBmi();
  }

  validateForm() {
    if (!this.hasSubmitButtonTarget) return;
    const submitButton = this.submitButtonTarget;

    const isValid = this.allRequiredFieldsValid();
    submitButton.disabled = !isValid;

    if (isValid) {
      submitButton.classList.remove("opacity-50", "cursor-not-allowed");
    } else {
      submitButton.classList.add("opacity-50", "cursor-not-allowed");
    }
  }

  allRequiredFieldsValid() {
    const requiredQuestions = this.questionTargets.filter((q) => q.dataset.required === "true");

    for (const questionEl of requiredQuestions) {
      // Skip hidden conditional questions - they're not required when hidden
      if (this.isHiddenConditionalQuestion(questionEl)) {
        continue;
      }

      if (!this.questionHasValue(questionEl)) {
        return false;
      }
    }

    return true;
  }

  isHiddenConditionalQuestion(questionEl) {
    // Check if this question is inside a hidden conditional-question wrapper
    const conditionalWrapper = questionEl.closest(".conditional-question");
    return conditionalWrapper && conditionalWrapper.classList.contains("hidden");
  }

  questionHasValue(questionEl) {
    // Check radio buttons
    const radios = questionEl.querySelectorAll('input[type="radio"]');
    if (radios.length > 0) {
      return Array.from(radios).some((r) => r.checked);
    }

    // Check checkboxes (at least one must be checked for required checkbox sets)
    const checkboxes = questionEl.querySelectorAll('input[type="checkbox"]');
    if (checkboxes.length > 0) {
      return Array.from(checkboxes).some((c) => c.checked);
    }

    // Check select fields
    const selects = questionEl.querySelectorAll("select");
    if (selects.length > 0) {
      return Array.from(selects).every((s) => s.value !== "");
    }

    // Check text inputs and textareas
    const textInputs = questionEl.querySelectorAll(
      'input[type="text"], input[type="number"], input[type="tel"], input[type="email"], textarea',
    );
    if (textInputs.length > 0) {
      return Array.from(textInputs).every((i) => i.value.trim() !== "");
    }

    // Check file inputs
    const fileInputs = questionEl.querySelectorAll('input[type="file"]');
    if (fileInputs.length > 0) {
      // For file inputs, check if there's already an uploaded file or a new file selected
      const hasExistingFile = questionEl.querySelector(".uploaded-file-name");
      return Array.from(fileInputs).some((f) => f.files && f.files.length > 0) || hasExistingFile;
    }

    // Check hidden inputs with value (for date fields that use hidden inputs)
    const hiddenWithValue = questionEl.querySelectorAll('input[type="hidden"]');
    if (hiddenWithValue.length > 0) {
      const visibleInputs = questionEl.querySelectorAll("input:not([type='hidden']), select, textarea");
      // If there are no visible inputs but there are hidden ones, consider it valid if hidden has value
      if (visibleInputs.length === 0) {
        return Array.from(hiddenWithValue).some((h) => h.value.trim() !== "");
      }
    }

    // Default to true if no recognizable input types
    return true;
  }

  updateConditionalQuestions() {
    // Find all conditional questions by data attribute instead of using targets
    // This allows multiple conditional questions per step with unique triggers
    const conditionalElements = this.element.querySelectorAll("[data-conditional-trigger]");

    conditionalElements.forEach((conditionalEl) => {
      const triggerValue = conditionalEl.dataset.conditionalTrigger;
      const expectedAnswer = conditionalEl.dataset.conditionalAnswer;

      if (!triggerValue || expectedAnswer === undefined) return;

      // Support multiple triggers separated by |
      const triggerSlugs = triggerValue.split("|").map((t) => t.trim());

      // Check if any trigger matches
      const shouldShow = triggerSlugs.some((triggerSlug) => {
        const currentValue = this.getQuestionValue(triggerSlug);
        return this.valueMatches(currentValue, expectedAnswer);
      });

      if (shouldShow) {
        conditionalEl.classList.remove("hidden");
        // Restore required attribute on form elements that should be required when visible
        this.updateConditionalRequiredState(conditionalEl, true);
      } else {
        conditionalEl.classList.add("hidden");
        // Remove required attribute from hidden form elements
        this.updateConditionalRequiredState(conditionalEl, false);
        // Clear any values in the hidden conditional question
        this.clearQuestionValue(conditionalEl);
      }
    });

    // Re-validate after updating conditional questions
    this.validateForm();
  }

  // Update the required state of form elements within a conditional question
  updateConditionalRequiredState(conditionalEl, isVisible) {
    const formElements = conditionalEl.querySelectorAll("input, select, textarea");
    const containerRequired = conditionalEl.dataset.conditionalRequired === "true";

    formElements.forEach((element) => {
      if (isVisible) {
        // Restore required if container has data-conditional-required="true"
        if (containerRequired) {
          element.required = true;
        }
      } else {
        // Remove required when hidden
        element.required = false;
      }
    });
  }

  // Toggle visibility of "more info" containers based on associated checkbox/radio/select state
  updateMoreInfoContainers() {
    this.moreInfoTargets.forEach((container) => {
      const triggerValues = container.dataset.moreInfoTrigger;
      if (!triggerValues) return;

      // Find the parent question element to scope our search
      const questionEl = container.closest("[data-intake-form-target='question']");
      if (!questionEl) return;

      // Parse trigger values (comma-separated list of values that should show this container)
      const triggers = triggerValues.split(",").map((t) => t.trim());

      // Check checkboxes and radios within the same question
      const checkedInputs = questionEl.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked');
      const checkedValues = Array.from(checkedInputs).map((input) => input.value);

      // Check select fields within the same question
      const selectFields = questionEl.querySelectorAll("select");
      const selectValues = Array.from(selectFields)
        .map((select) => select.value)
        .filter((value) => value !== "");

      // Combine all values
      const allValues = [...checkedValues, ...selectValues];

      // Show container if any trigger value matches
      const shouldShow = triggers.some((trigger) => allValues.includes(trigger));

      // Get the textarea/input inside the container
      const inputField = container.querySelector("textarea, input, select");

      if (shouldShow) {
        container.classList.remove("hidden");
        if (inputField) {
          inputField.required = true;
        }
      } else {
        container.classList.add("hidden");
        if (inputField) {
          inputField.required = false;
          inputField.value = "";
        }
      }
    });

    // Re-validate after updating more info containers
    this.validateForm();
  }

  // Initialize exclusive answer checkbox behavior
  initExclusiveAnswers() {
    const exclusiveCheckboxes = this.element.querySelectorAll('input[type="checkbox"].exclusive-answer');

    exclusiveCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", (event) => {
        this.handleExclusiveAnswerChange(event);
      });
    });

    // Also handle when a non-exclusive checkbox is checked - uncheck exclusive answers
    const allCheckboxes = this.element.querySelectorAll('input[type="checkbox"]');
    allCheckboxes.forEach((checkbox) => {
      if (!checkbox.classList.contains("exclusive-answer")) {
        checkbox.addEventListener("change", (event) => {
          this.handleNonExclusiveAnswerChange(event);
        });
      }
    });
  }

  // When an exclusive answer is checked, uncheck all other checkboxes in the same question
  handleExclusiveAnswerChange(event) {
    const checkbox = event.target;
    if (!checkbox.checked) return;

    // Find the parent question element
    const questionEl = checkbox.closest("[data-intake-form-target='question']");
    if (!questionEl) return;

    // Get all other checkboxes in the same question
    const otherCheckboxes = questionEl.querySelectorAll('input[type="checkbox"]:not(.exclusive-answer)');

    // Uncheck all non-exclusive checkboxes
    otherCheckboxes.forEach((otherCheckbox) => {
      otherCheckbox.checked = false;
    });

    // Also uncheck other exclusive answers (in case there are multiple)
    const otherExclusiveCheckboxes = questionEl.querySelectorAll('input[type="checkbox"].exclusive-answer');
    otherExclusiveCheckboxes.forEach((otherExclusive) => {
      if (otherExclusive !== checkbox) {
        otherExclusive.checked = false;
      }
    });

    // Update disclaimers and more info containers
    this.updateMoreInfoContainers();
    this.updateDisclaimerContainers();
    this.validateForm();
  }

  // When a non-exclusive checkbox is checked, uncheck any exclusive answers
  handleNonExclusiveAnswerChange(event) {
    const checkbox = event.target;
    if (!checkbox.checked) return;

    // Find the parent question element
    const questionEl = checkbox.closest("[data-intake-form-target='question']");
    if (!questionEl) return;

    // Uncheck all exclusive answer checkboxes in the same question
    const exclusiveCheckboxes = questionEl.querySelectorAll('input[type="checkbox"].exclusive-answer');

    exclusiveCheckboxes.forEach((exclusiveCheckbox) => {
      exclusiveCheckbox.checked = false;
    });

    // Update disclaimers and more info containers
    this.updateMoreInfoContainers();
    this.updateDisclaimerContainers();
    this.validateForm();
  }

  // Toggle visibility of disclaimer containers based on associated checkbox/radio state
  updateDisclaimerContainers() {
    this.disclaimerTargets.forEach((container) => {
      const triggerValue = container.dataset.disclaimerTrigger;
      if (!triggerValue) return;

      // Find the parent question element to scope our search
      const questionEl = container.closest("[data-intake-form-target='question']");
      if (!questionEl) return;

      // Check checkboxes and radios within the same question
      const checkedInputs = questionEl.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked');
      const checkedValues = Array.from(checkedInputs).map((input) => input.value);

      // Show container if the trigger value is checked
      const shouldShow = checkedValues.includes(triggerValue);

      if (shouldShow) {
        container.classList.remove("hidden");
      } else {
        container.classList.add("hidden");
      }
    });
  }

  getQuestionValue(slug) {
    // Find the question element by slug
    const questionEl = this.questionTargets.find((q) => q.dataset.questionSlug === slug);

    if (!questionEl) return null;

    // Check radio buttons
    const checkedRadio = questionEl.querySelector('input[type="radio"]:checked');
    if (checkedRadio) return checkedRadio.value;

    // Check checkboxes (return array of checked values)
    const checkboxes = questionEl.querySelectorAll('input[type="checkbox"]:checked');
    if (checkboxes.length > 0) {
      return Array.from(checkboxes).map((c) => c.value);
    }

    // Check select
    const select = questionEl.querySelector("select");
    if (select) return select.value;

    // Check text input
    const textInput = questionEl.querySelector('input[type="text"], input[type="number"], textarea');
    if (textInput) return textInput.value;

    return null;
  }

  valueMatches(currentValue, expectedAnswer) {
    // Handle wildcard "*" - matches any non-empty value
    if (expectedAnswer === "*") {
      if (currentValue === null || currentValue === undefined) return false;
      if (Array.isArray(currentValue)) return currentValue.length > 0;
      return String(currentValue).trim() !== "";
    }

    if (currentValue === null || currentValue === undefined) return false;

    // Handle array of checkbox values
    if (Array.isArray(currentValue)) {
      // Support comma-separated expected answers
      const expectedAnswers = expectedAnswer.split(",").map((a) => a.trim());
      return expectedAnswers.some((expected) => currentValue.includes(expected));
    }

    // Support comma-separated expected answers for single values too
    const expectedAnswers = expectedAnswer.split(",").map((a) => a.trim());
    return expectedAnswers.includes(currentValue);
  }

  clearQuestionValue(conditionalEl) {
    // Clear radios
    conditionalEl.querySelectorAll('input[type="radio"]:checked').forEach((r) => (r.checked = false));

    // Clear checkboxes
    conditionalEl.querySelectorAll('input[type="checkbox"]:checked').forEach((c) => (c.checked = false));

    // Clear selects
    conditionalEl.querySelectorAll("select").forEach((s) => (s.selectedIndex = 0));

    // Clear text inputs
    conditionalEl.querySelectorAll('input[type="text"], input[type="number"], textarea').forEach((i) => (i.value = ""));
  }

  // File upload UI handling
  initFileUploadHandlers() {
    const fileInputs = this.element.querySelectorAll('input[type="file"]');

    fileInputs.forEach((input) => {
      input.addEventListener("change", (event) => {
        this.handleFileInputChange(event);
      });
    });
  }

  handleFileInputChange(event) {
    const input = event.target;
    const container = input.closest("[data-upload-container]");
    if (!container) return;

    const noFileContainer = container.querySelector(".no-file-container");
    const fileUploadedContainer = container.querySelector(".file-uploaded-container");
    const beforeProceedContainer = container.querySelector(".before-proceed");
    const fileNameEl = container.querySelector(".file-name");

    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const fileName = file.name;

      // Update the preview image with the selected file
      const previewImage = container.querySelector(".wizard-upload-image");
      if (previewImage && file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          previewImage.src = e.target.result;
          previewImage.classList.remove("wizard-upload-image--placeholder");
          previewImage.classList.add("wizard-upload-image--uploaded");
        };
        reader.readAsDataURL(file);
      }

      // Hide the "no file" notice
      if (noFileContainer) {
        noFileContainer.classList.add("hidden");
      }

      // Hide the existing "file uploaded" notice (from server-rendered state)
      if (fileUploadedContainer) {
        fileUploadedContainer.classList.add("hidden");
      }

      // Show the success notice with filename
      if (beforeProceedContainer) {
        beforeProceedContainer.classList.remove("hidden");
        if (fileNameEl) {
          fileNameEl.textContent = fileName;
        }
      }

      // Update the label to show "Change File"
      const label = container.querySelector("label");
      if (label) {
        label.classList.add("has-file");
        const buttonText = label.querySelector(".wizard-btn-primary");
        if (buttonText) {
          // Update just the text, preserving the SVG
          const textNode = Array.from(buttonText.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
          if (textNode) {
            textNode.textContent = "Change File";
          }
        }
      }
    }

    this.validateForm();
  }

  // BMI Calculator functionality
  isBmiStep(wizardContainer) {
    const stepName = wizardContainer.dataset.stepName;
    return stepName === "current_bmi_info" || stepName === "bmi_check";
  }

  initBmiCalculator() {
    const wizardContainer = document.querySelector(".wizard-container");
    if (!wizardContainer) return;
    if (!this.isBmiStep(wizardContainer)) return;

    this.updateBmi();
  }

  updateBmi() {
    const wizardContainer = document.querySelector(".wizard-container");
    if (!wizardContainer) return;
    if (!this.isBmiStep(wizardContainer)) return;

    const bmiContentEl = this.getBmiContentElement();
    if (!bmiContentEl) return;

    const heightInches = this.getHeightInInches();
    const weightLbs = this.getWeightInPounds();

    bmiContentEl.innerHTML = this.getBmiMessage(heightInches, weightLbs);

    // Update hidden BMI input field with the calculated value
    this.updateBmiHiddenField(heightInches, weightLbs);
  }

  updateBmiHiddenField(heightInches, weightLbs) {
    const bmiQuestion = this.questionTargets.find(
      (q) => q.dataset.questionSlug === "bmi" || q.dataset.questionSlug === "fu_bmi",
    );
    if (!bmiQuestion) return;

    const hiddenInput = bmiQuestion.querySelector('input[type="hidden"]');
    if (!hiddenInput) return;

    if (heightInches !== null && weightLbs !== null) {
      const bmi = this.calculateBmi(heightInches, weightLbs);
      hiddenInput.value = bmi.toFixed(1);
    } else {
      hiddenInput.value = "";
    }
  }

  getBmiContentElement() {
    const bmiQuestion = this.questionTargets.find(
      (q) => q.dataset.questionSlug === "bmi_display" || q.dataset.questionSlug === "fu_bmi_display",
    );
    if (!bmiQuestion) return null;
    return bmiQuestion.querySelector(".wizard-info-content");
  }

  getHeightInInches() {
    const heightQuestion = this.questionTargets.find(
      (q) => q.dataset.questionSlug === "height" || q.dataset.questionSlug === "fu_height",
    );
    if (!heightQuestion) return null;

    const select = heightQuestion.querySelector("select");
    if (!select || !select.value) return null;

    return this.parseHeightToInches(select.value);
  }

  parseHeightToInches(heightStr) {
    // Parse format like "5'10\"" or "6'2\""
    const match = heightStr.match(/^(\d+)'(\d+)"?$/);
    if (!match) return null;

    const feet = parseInt(match[1], 10);
    const inches = parseInt(match[2], 10);
    return feet * 12 + inches;
  }

  getWeightInPounds() {
    const weightQuestion = this.questionTargets.find(
      (q) => q.dataset.questionSlug === "weight" || q.dataset.questionSlug === "fu_weight",
    );
    if (!weightQuestion) return null;

    const input = weightQuestion.querySelector('input[type="number"]');
    if (!input || !input.value) return null;

    const weight = parseFloat(input.value);
    return isNaN(weight) ? null : weight;
  }

  calculateBmi(heightInches, weightLbs) {
    // BMI = (weight in pounds * 703) / (height in inches)^2
    return (weightLbs * 703) / (heightInches * heightInches);
  }

  buildNotice(text, type = "warning") {
    return `<div class="wizard-notice wizard-notice-${type} mt-3">
      <svg class="wizard-notice-icon w-5 h-5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
      </svg>
      <span class="wizard-notice-text">${text}</span>
    </div>`;
  }

  getBmiMessage(heightInches, weightLbs) {
    const hasHeight = heightInches !== null;
    const hasWeight = weightLbs !== null;

    if (!hasHeight && !hasWeight) {
      return "";
    }

    if (hasHeight && !hasWeight) {
      return this.buildNotice("Please enter your weight to calculate your current BMI.", "info");
    }

    if (!hasHeight && hasWeight) {
      return this.buildNotice("Please enter your height to calculate your current BMI.", "info");
    }

    const bmi = this.calculateBmi(heightInches, weightLbs);
    const bmiFormatted = bmi.toFixed(1);

    let message = `Your current BMI is ${bmiFormatted}.`;

    // if (bmi < 25) {
    //   message += this.buildNotice("You may not be eligible for weight loss medication with a BMI < 25.")
    // }

    return this.buildNotice(message, "info");
  }
}
